/*
 * Copyright (c) 2020, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useEffect, useMemo, useState } from 'react'
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation } from "react-router-dom";
import { useAuthContext } from '@asgardeo/auth-react';
import { useIntl } from 'react-intl';

import { CssBaseline, Container } from '@material-ui/core';

import {
    fetchUserOrganizations,
    userLogin,
    changeUserOrganization,
    obtainApimToken,
    acceptInvitation,
    addEnterpriseUserToOrg
} from '../../data/operations';
import { ApplicationState, StorageOperations, UserReducer, Organization } from '../../data/interfaces';
import { localStoreOperation, sessionStoreOperation } from '../../data/utils';
import {
    FIDP, RESOURCE_LOGIN, RETURN_TO_PATH, MAX_RETRY_COUNT,
    SELECTED_ORG_UUID, VALUE_ZERO, VALUE_ONE, AUTHORIZATION_ERROR, CORRELATION_ID
} from '../../data/constants';
import { useStyles } from './styles';
import { PreLoaderLogo } from '../../elements/PreLoaderLogo';
import * as Utils from '../../data/utils';
import * as Constants from '../../data/constants';
import Settings from '../../conf/settings';
import {setErrorNotification} from "../../store/actions/notification";
import { trackLoginPageLoad } from '../../util/app-insights';

export interface IAcceptInvitation {
    status: string;
    message: string;
}

interface SignInProps {
    userReducer: UserReducer,
    userLogin: (fidp?: string, state?: string, correlationId?: string, orgName?: string, enterpriseEmail?:string) => void,
    fetchUserOrganizations: (correlationId: string) => Promise<void>,
    addEnterpriseUserToOrg: () => Promise<void>,
    changeUserOrganization: (selectedOrganization: Organization) => void,
    obtainApimToken: (orgHandle: string, correlationId: string) => Promise<void>
}

function SignIn(props: SignInProps) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { state } = useAuthContext();
    const [retryCount, setRetryCount] = useState(0);
    const [invitationAccepted, setInvitationAccepted] = useState(false);
    const {
        signedIn, orgs: userOrganizations, loading: userReducerLoading, userInfo,
        apimTokenObtained, apimTokenObtaining, error, selectedOrg, status
    } = props.userReducer;
    const history = useHistory();
    const location = useLocation<{confirmationOrg: string; confirmationKey: string; enterpriseEmail: string}>();
    const confirmationOrg = location.state?.confirmationOrg;
    const confirmationKey = location.state?.confirmationKey;
    const enterpriseEmail = location.state?.enterpriseEmail;
    const params = new URLSearchParams(location.search);
    const authCode = params.get("code");
    const errorCode = params.get("error");
    const stateQueryParam = params.get("state") || '';
    const orgName = localStoreOperation(StorageOperations.GET, Constants.SELECTED_ORG_HANDLE_NAME) || '';
    const correlationId = localStoreOperation(StorageOperations.GET, CORRELATION_ID) || '';
    const clientErrorStatusCodes = [401, 404];
    const successStatusCodesToRedirectToUnauthorizedPage = [202];
    const errorCodesToAddEnterpriseUserToOrg = [1003, 1020, 1023];
    const errorCodesToRedirectToUnauthorizedPage = [1020, 1021, 1022];

    const [isInvitation, stateCustomDomain, stateConfirmationOrg, stateConfirmationKey] = useMemo(() => {
        if (!stateQueryParam) {
            return [false, undefined, undefined, undefined];
        }
        let stateString;
        try {
            stateString = atob(stateQueryParam);
        } catch (e) {
            return [false, undefined, undefined, undefined];
        }

        let confirmationOrg;
        let confirmationKey;
        let customDomain;
        let isInvitation;

        const stateObj = JSON.parse(stateString);
        if (stateString.includes('confirmationKey') && stateString.includes('confirmationOrg') && stateString
                .includes('customDomain')) {
            confirmationOrg = stateObj.confirmationOrg;
            confirmationKey = stateObj.confirmationKey;
            customDomain = stateObj.customDomain;
            isInvitation = true;
        } else if (stateString.includes('confirmationKey') && stateString.includes('confirmationOrg') && !stateString
                .includes('customDomain')) {
            confirmationOrg = stateObj.confirmationOrg;
            confirmationKey = stateObj.confirmationKey;
            customDomain = undefined;
            isInvitation = true;
        } else if ((!stateString.includes('confirmationKey') || !stateString.includes('confirmationOrg')) && stateString
                .includes('customDomain')) {
            confirmationOrg = undefined;
            confirmationKey = undefined;
            customDomain = stateObj.customDomain;
            isInvitation = false;
        } else {
            confirmationOrg = undefined;
            confirmationKey = undefined;
            customDomain = undefined;
            isInvitation = false;
        }

        return [isInvitation, customDomain, confirmationOrg, confirmationKey];
    }, [stateQueryParam]);

    const extractOrgHandleFromPath = (path: string | null | undefined): string => {
        if (!path) {
            return '';
        }
        const result = path.match('^/([^/]+)');
        if (result && result.length > 0) {
            return result[1];
        }
        return '';
    }

    const replaceWithCustomDomain = () => {
        const currentHost = window.location.hostname;
        const isCustomDomainCurrently = currentHost !== window.Settings.devportalHost;
        if (!isCustomDomainCurrently && stateCustomDomain && window.location.search.includes("code") && authCode !== null) {
            window.location.replace(window.location.href.replace(Settings.devportalHost, stateCustomDomain));
        }
    };

    useEffect(() => {
        if (errorCode === "microsoft_email_not_found") {
            history.push('/client-error');
            return;
        }
        if (errorCode === Constants.LOGIN_REQUIRED) {
            // login_required means no active IDP session, not a fatal error.
            // For enterprise IDP the username is required; clear the stored fidp so the
            // login page collects it instead of looping back here without it.
            if (localStoreOperation(StorageOperations.GET, FIDP) === Settings.enterpriseFidp) {
                localStoreOperation(StorageOperations.REMOVE, FIDP);
            }
            history.push(RESOURCE_LOGIN);
            return;
        }
        if (!state.isAuthenticated) {
            replaceWithCustomDomain();
            const customDomain = Settings.customDomain;
            let fidp = localStoreOperation(StorageOperations.GET, FIDP);
            // @TODO Starting temporary change during the transition from Choreo IDP to Asgardeo
            if (fidp === Settings.oldGoogleFidp) {
                fidp = Settings.googleFidp
            } else if (fidp === Settings.oldGithubFidp) {
                fidp = Settings.githubFidp
            }
            localStoreOperation(StorageOperations.ADD, FIDP, fidp ?? "")
            // Ending temporary change
            if (fidp && !apimTokenObtaining) {
                const stateObj: { [key: string]: string } = {};
                if (confirmationKey && confirmationOrg && customDomain) {
                    stateObj.confirmationKey = confirmationKey;
                    stateObj.confirmationOrg = confirmationOrg;
                    stateObj.customDomain = customDomain;
                } else if (confirmationKey && confirmationOrg && !customDomain) {
                    stateObj.confirmationKey = confirmationKey;
                    stateObj.confirmationOrg = confirmationOrg;
                } else if ((!confirmationKey || !confirmationOrg) && customDomain) {
                    stateObj.customDomain = customDomain;
                }

                const stateString = btoa(JSON.stringify(stateObj));
                props.userLogin(fidp, stateString, correlationId, orgName, enterpriseEmail);
            } else {
                history.push(RESOURCE_LOGIN);
                trackLoginPageLoad(correlationId);
            }
        }
    }, []);

    useEffect(() => {
        if (!signedIn || !isInvitation) {
            return;
        }

        acceptInvitation(stateConfirmationOrg, stateConfirmationKey)
            ?.then((response) => {
                const acceptInvitation = response?.data as IAcceptInvitation;
                if (acceptInvitation.status === "200") {
                    setInvitationAccepted(true);
                } else {
                    dispatch(setErrorNotification(intl.formatMessage({
                        id: "signIn.messages.acceptInvitation.failed",
                        defaultMessage: "Failed to accept the invitation"
                    })));
                }
            })
            .catch(() => dispatch(setErrorNotification(intl.formatMessage({
                id: "signIn.messages.acceptInvitation.failed",
                defaultMessage: "Failed to accept the invitation"
            }))));
    }, [signedIn, isInvitation, stateConfirmationOrg, stateConfirmationKey]);

    useEffect(() => {
        if (isInvitation && !invitationAccepted) {
            return;
        }
        replaceWithCustomDomain();

        if (successStatusCodesToRedirectToUnauthorizedPage.some(o => o === status)) {
            history.push(AUTHORIZATION_ERROR + 1021);
        }

        if (signedIn && userOrganizations?.length > VALUE_ZERO && !apimTokenObtaining) {
            const returnTo = sessionStoreOperation(StorageOperations.GET, RETURN_TO_PATH);
            const returnToOrgHandle = extractOrgHandleFromPath(returnTo);
            const selectedOrgUuidFromStorage = localStoreOperation(StorageOperations.GET, SELECTED_ORG_UUID);
            let currentUserOrg: Organization;
            if (userOrganizations.length === VALUE_ONE) {
                currentUserOrg = userOrganizations[VALUE_ZERO];
                props.changeUserOrganization(currentUserOrg);
            } else if (returnToOrgHandle) {
                currentUserOrg = userOrganizations.find((userOrg: Organization) =>
                    userOrg.handle === returnToOrgHandle) || userOrganizations[VALUE_ZERO];
                props.changeUserOrganization(currentUserOrg);
            } else if (selectedOrgUuidFromStorage) {
                currentUserOrg = userOrganizations.find((userOrg: Organization) =>
                    userOrg.uuid === selectedOrgUuidFromStorage) || userOrganizations[VALUE_ZERO];
                props.changeUserOrganization(currentUserOrg);
            } else {
                currentUserOrg = userOrganizations.find((userOrg: Organization) =>
                    userOrg.name === userInfo?.name) || userOrganizations[VALUE_ZERO];
                props.changeUserOrganization(currentUserOrg);
            }
            if(selectedOrg.handle && !apimTokenObtained) {
                props.obtainApimToken(selectedOrg.handle, correlationId);
            }
            if (apimTokenObtained) {
                Utils.localStoreOperation(StorageOperations.ADD, Constants.SELECTED_ORG_HANDLE_NAME, currentUserOrg.handle);
                if (returnTo) {
                    sessionStoreOperation(StorageOperations.REMOVE, RETURN_TO_PATH);
                    history.push(returnTo);
                } else {
                    history.push('/' + currentUserOrg.handle);
                }
            }
        } else if (signedIn && !apimTokenObtaining && !apimTokenObtained && userOrganizations?.length < 1 && !userReducerLoading && retryCount < MAX_RETRY_COUNT) {
            setRetryCount(retryCount + 1);
            if (typeof error === "object" && clientErrorStatusCodes.some(o => o === error?.response?.status) && userInfo?.is_eidp === "true") {
                if (errorCodesToAddEnterpriseUserToOrg.some(o => o === error?.response?.data?.code)) {
                    props.addEnterpriseUserToOrg();
                }
                if (errorCodesToRedirectToUnauthorizedPage.some(o => o === error?.response?.data?.code)) {
                    history.push(AUTHORIZATION_ERROR + error?.response?.data?.code);
                }
            } else {
                props.fetchUserOrganizations(correlationId);
            }
        } else if (retryCount >= MAX_RETRY_COUNT) {
            let err_code = "";
            if ( typeof error === "object"){
                err_code = error?.response?.data?.code ? error?.response?.data?.code : "";
            }
            history.push(AUTHORIZATION_ERROR + err_code);
        }
    }, [props.userReducer, isInvitation, invitationAccepted])

    return (
      <div>
        <CssBaseline />
        <Container maxWidth="lg" className={classes.signInContainer}>
          <div className={classes.loaderContainer}>
            <PreLoaderLogo size="medium" />
          </div>
        </Container>
      </div>
    );
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        userReducer: state.userReducer
    }
}

const mapDispatchToProps = {
    userLogin: userLogin,
    fetchUserOrganizations: fetchUserOrganizations,
    addEnterpriseUserToOrg: addEnterpriseUserToOrg,
    changeUserOrganization: changeUserOrganization,
    obtainApimToken: obtainApimToken
}

export default connect(mapStateToProps, mapDispatchToProps)(SignIn);
